/**
 * Chat shell for a new article.
 * Path A and path B share the topic, country and language, keyword, template, tone, and the review.
 * Path B starts with the article URL. After that address is read, AI Lucy shows the page title
 * and the headings that stayed. The chat only reads the existing GetScrapped result.
 * Path B then asks what the edit should do. That choice is stored with the article.
 * The chat does not call a model and does not rewrite blocks.
 * The article page still owns the scrape and the failed-scrape dialog.
 * The review hands the choice to the article page.
 */
import {LocationLanguages} from '@/enum/locations_languages';
import {
    AUDIENCE_IDS,
    DEPTH_IDS,
    defaultWritingBrief,
    TONE_IDS,
    type ArticleAudienceId,
    type ArticleDepthId,
    type ArticleToneId,
    type GeoTemplateId,
    normalizeGeoTemplate,
} from '@/config/WritingBrief';
import {normalizeKeywordPhrase} from '@/service/keywordInflection';
import {importScrapedBlocks, type HeadingRole} from '@/service/articleSanitize';

export const ARTICLE_CHAT_TOPIC_MAX_WORDS = 200;
export const ARTICLE_CHAT_PATH_A_STEPS = 5;
export const ARTICLE_CHAT_PATH_B_STEPS = 8;
export const ARTICLE_CHAT_TOPIC_STEP = 1;
export const ARTICLE_CHAT_DRAFT_KEY = 'seowritee.articleChat.draft.v1';
export const ARTICLE_CHAT_ANALYZE_DRAFT_KEY = 'seowritee.articleChat.analyze.v1';
export const ARTICLE_CHAT_ASKING_MS = 500;
export const ARTICLE_CHAT_SUGGESTION_DELAY_MS = 300;

export const TOPIC_CHIP_IDS = [
    'beginnerGuide',
    'typeComparison',
    'costCalculation',
    'prosCons',
    'practicalTips',
    'faq',
] as const;

export const ARTICLE_CHAT_DEFAULT_LOCATION = '2203';
export const ARTICLE_CHAT_DEFAULT_LANGUAGE = 'cs';

export type TopicChipId = typeof TOPIC_CHIP_IDS[number];
export type ArticleChatStepId = 'url' | 'loaded' | 'topic' | 'locale' | 'keyword' | 'template' | 'style' | 'goal';
export type ArticleChatDraftPhase = ArticleChatStepId | 'summary' | 'form';
export type CreateSetupPhase = 'resume' | ArticleChatDraftPhase;
export type ArticleChatReturnTo = ArticleChatStepId | 'summary' | null;
export type ArticleUrlState = 'empty' | 'protocol' | 'invalid' | 'ready';

export type LoadedHeading = {
    level: number;
    text: string;
};

export type LoadedPageSummary = {
    title: string;
    headings: LoadedHeading[];
};

export const ARTICLE_CHAT_FLOW: ArticleChatStepId[] = ['topic', 'locale', 'keyword', 'template', 'style'];
export const ARTICLE_CHAT_ANALYZE_FLOW: ArticleChatStepId[] = ['url', 'loaded', 'topic', 'locale', 'keyword', 'template', 'style', 'goal'];

export const IMPROVE_GOAL_IDS = ['polish', 'fill', 'rewrite'] as const;
export type ImproveGoalId = typeof IMPROVE_GOAL_IDS[number];
export const DEFAULT_IMPROVE_GOAL: ImproveGoalId = 'polish';

export type GoalSection = {
    id: string;
    text: string;
};

const PHASE_ORDER: ArticleChatDraftPhase[] = ['url', 'loaded', ...ARTICLE_CHAT_FLOW, 'goal', 'summary', 'form'];

export type ArticleChatSuggestion = {
    keyword: string;
    monthly_searches_avg: number;
};

export type ArticleChatDraft = {
    aiPrompt: string;
    phase: ArticleChatDraftPhase;
    locationCode: string;
    languageCode: string;
    returnTo: ArticleChatReturnTo;
    keyword: string;
    geoTemplate: GeoTemplateId;
    geoTemplateUserPicked: boolean;
    tone: ArticleToneId;
    audience: ArticleAudienceId;
    depth: ArticleDepthId;
    secondaryKeywords: string[];
    url: string;
    loadedTitle: string;
    loadedHeadings: LoadedHeading[];
    loadedUrl: string;
    /** URL read returned no outline (anti-scrape empty extract). */
    urlScrapeExtractionFailed?: boolean;
    improveGoal: ImproveGoalId;
    sectionUids: string[];
};

export type HistoryRow = {
    id: string;
    step: number;
    title: string;
    value: string;
};

export type HistoryPresentation = {
    folded: boolean;
    from: number;
    to: number;
    visible: HistoryRow[];
    panel: HistoryRow[];
};

export type TopicFieldState = 'empty' | 'over' | 'ready';

type DraftStorage = {
    getItem?: (key: string) => string | null;
    setItem?: (key: string, value: string) => void;
    removeItem?: (key: string) => void;
};

/** Same split as rules.maxWordsRule, with a blank field shown as zero words. */
export function topicWordCount(value: string): number {
    const text = String(value ?? '');
    if (!text.trim()) {
        return 0;
    }
    return text.split(' ').length;
}

export function topicFieldState(value: string): TopicFieldState {
    const text = String(value ?? '');
    if (!text.trim()) {
        return 'empty';
    }
    if (!(text.split(' ').length < ARTICLE_CHAT_TOPIC_MAX_WORDS)) {
        return 'over';
    }
    return 'ready';
}

export function canContinueTopic(value: string): boolean {
    return topicFieldState(value) === 'ready';
}

/** Same checks as validUrlRule plus a required address. Empty is not ready. */
export function articleUrlState(value: string): ArticleUrlState {
    const text = String(value ?? '').trim();
    if (!text) {
        return 'empty';
    }
    if (!text.startsWith('http://') && !text.startsWith('https://')) {
        return 'protocol';
    }
    try {
        const parsed = new URL(text);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
            return 'protocol';
        }
        return 'ready';
    } catch {
        return 'invalid';
    }
}

export function articleUrlReady(value: string): boolean {
    return articleUrlState(value) === 'ready';
}

type ScrapeHeadingSource = {
    status?: number;
    metaTitle?: string;
    h1h?: unknown[] | null;
    h1p?: unknown[] | null;
    h2h?: unknown[] | null;
    h2p?: unknown[] | null;
    h3h?: unknown[] | null;
    h3p?: unknown[] | null;
    h4h?: unknown[] | null;
    h4p?: unknown[] | null;
    h5h?: unknown[] | null;
    h5p?: unknown[] | null;
    h6h?: unknown[] | null;
    h6p?: unknown[] | null;
};

/**
 * Title and headings kept by the same import the article page uses.
 * A failed or empty read returns null. The chat does not invent a second error.
 */
export function loadedPageFromScrape(response: ScrapeHeadingSource | null | undefined): LoadedPageSummary | null {
    if (!response || response.status !== 200) {
        return null;
    }
    const headings: LoadedHeading[] = [];
    for (let level = 1; level <= 6; level++) {
        const role: HeadingRole = level === 1 ? 'h1' : level === 2 ? 'h2' : 'h3';
        const source = response as Record<string, unknown>;
        const blocks = importScrapedBlocks(
            source['h' + level + 'h'] as unknown[] | null | undefined,
            source['h' + level + 'p'] as unknown[] | null | undefined,
            '',
            role,
        );
        blocks.forEach((block) => {
            const text = String(block.h || '').replace(/\s+/g, ' ').trim();
            if (text) {
                headings.push({level, text});
            }
        });
    }
    if (!headings.length) {
        return null;
    }
    const meta = String(response.metaTitle || '').replace(/\s+/g, ' ').trim();
    const h1 = headings.find((item) => item.level === 1);
    return {
        title: meta || (h1 ? h1.text : ''),
        headings,
    };
}

/** H2 headings the user can mark for a rewrite. The id follows the loaded outline. */
export function goalSectionsFromHeadings(headings: LoadedHeading[] | null | undefined): GoalSection[] {
    const sections: GoalSection[] = [];
    (headings || []).forEach((item, index) => {
        if (!item || item.level !== 2) {
            return;
        }
        const text = String(item.text || '').replace(/\s+/g, ' ').trim();
        if (!text) {
            return;
        }
        sections.push({id: 'h2-' + index, text});
    });
    return sections;
}

export function normalizeImproveGoal(value: unknown): ImproveGoalId {
    return typeof value === 'string' && (IMPROVE_GOAL_IDS as readonly string[]).includes(value)
        ? value as ImproveGoalId
        : DEFAULT_IMPROVE_GOAL;
}

export function normalizeSectionUids(value: unknown, sections: GoalSection[]): string[] {
    if (!Array.isArray(value)) {
        return [];
    }
    const allowed = new Set(sections.map((section) => section.id));
    const picked: string[] = [];
    value.forEach((item) => {
        const id = String(item || '').trim();
        if (!id || !allowed.has(id) || picked.indexOf(id) !== -1) {
            return;
        }
        picked.push(id);
    });
    return picked;
}

/** Polish and fill continue at once. A rewrite waits until one H2 is selected. */
export function goalCanContinue(
    goal: unknown,
    sectionUids: unknown,
    headings: LoadedHeading[] | null | undefined,
): boolean {
    if (normalizeImproveGoal(goal) !== 'rewrite') {
        return true;
    }
    const sections = goalSectionsFromHeadings(headings);
    return normalizeSectionUids(sectionUids, sections).length > 0;
}

/**
 * Same address with a summary returns to the open step.
 * A successful read shows what was loaded, then Continue returns to the open step.
 * A failed read skips the step. The article page keeps the failed-scrape dialog.
 */
export function urlContinuePlan(input: {
    returnTo: ArticleChatReturnTo;
    sameSummary: boolean;
    readOk: boolean;
}): {phase: Exclude<CreateSetupPhase, 'resume'>; returnTo: ArticleChatReturnTo} {
    const back = input.returnTo === 'loaded' || input.returnTo === 'url' ? null : input.returnTo;
    if (input.sameSummary && back) {
        return {phase: back, returnTo: null};
    }
    if (input.sameSummary || input.readOk) {
        return {phase: 'loaded', returnTo: back};
    }
    // Empty anti-scrape extract still opens the loaded step ("Co jsme načetli").
    // The article page failed-scrape dialog handles the refund.
    if (back) {
        return {phase: back, returnTo: null};
    }
    return {phase: 'loaded', returnTo: null};
}

export function articleChatDraftKey(isAnalyze: boolean): string {
    return isAnalyze ? ARTICLE_CHAT_ANALYZE_DRAFT_KEY : ARTICLE_CHAT_DRAFT_KEY;
}

export function chatFlow(isAnalyze: boolean): ArticleChatStepId[] {
    return isAnalyze ? ARTICLE_CHAT_ANALYZE_FLOW : ARTICLE_CHAT_FLOW;
}

export function appendTopicChip(current: string, phrase: string): string {
    const extra = String(phrase || '').replace(/\s+/g, ' ').trim();
    const base = String(current || '').replace(/\s+$/g, '');
    if (!extra) {
        return base;
    }
    if (base.includes(extra)) {
        return base;
    }
    if (!base.trim()) {
        return extra;
    }
    const joiner = /[.!?…]$/.test(base) ? ' ' : '. ';
    return `${base}${joiner}${extra}`;
}

export function firstQueryString(value: unknown): string {
    if (typeof value === 'string') {
        return value.trim();
    }
    if (Array.isArray(value)) {
        const first = value.find((item) => typeof item === 'string' && String(item).trim());
        return typeof first === 'string' ? first.trim() : '';
    }
    return '';
}

const DRAFT_PHASES: ArticleChatDraftPhase[] = [...PHASE_ORDER];

function phaseRank(phase: string): number {
    const index = PHASE_ORDER.indexOf(phase as ArticleChatDraftPhase);
    return index < 0 ? 0 : index;
}

function asChoice<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
    return typeof value === 'string' && (allowed as readonly string[]).includes(value)
        ? value as T
        : fallback;
}

function asReturnTo(value: unknown): ArticleChatReturnTo {
    if (
        value === 'summary'
        ||         value === 'url'
        || value === 'loaded'
        || value === 'topic'
        || value === 'locale'
        || value === 'keyword'
        || value === 'template'
        || value === 'style'
        || value === 'goal'
    ) {
        return value;
    }
    return null;
}

function asSectionUids(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }
    const picked: string[] = [];
    value.forEach((item) => {
        const id = String(item || '').trim();
        if (!/^h2-\d+$/.test(id) || picked.indexOf(id) !== -1) {
            return;
        }
        picked.push(id);
    });
    return picked;
}

function asLoadedHeadings(value: unknown): LoadedHeading[] {
    if (!Array.isArray(value)) {
        return [];
    }
    const headings: LoadedHeading[] = [];
    value.forEach((item) => {
        if (!item || typeof item !== 'object') {
            return;
        }
        const level = Number((item as {level?: unknown}).level);
        const text = String((item as {text?: unknown}).text || '').replace(/\s+/g, ' ').trim();
        if (!text || level < 1 || level > 6) {
            return;
        }
        headings.push({level, text});
    });
    return headings;
}

function asSecondaryKeywords(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value
        .map((item) => String(item || '').replace(/\s+/g, ' ').trim())
        .filter((item) => item.length > 0);
}

export function chatStepNumber(
    id: ArticleChatStepId,
    flow: readonly ArticleChatStepId[] = ARTICLE_CHAT_FLOW,
): number {
    const index = flow.indexOf(id);
    return index < 0 ? 1 : index + 1;
}

/** Blank keyword does not ask for similar phrases. */
export function suggestionRequest(keyword: string, locationCode: string): {keyword: string; locationCode: string} | null {
    const word = String(keyword || '').replace(/\s+/g, ' ').trim();
    const location = String(locationCode || '').trim();
    if (!word || !location) {
        return null;
    }
    return {keyword: word, locationCode: location};
}

export function keywordReady(value: string): boolean {
    return String(value || '').trim().length > 0;
}

/** Enter and Continue share this. The main keyword and duplicates stay out. */
export function commitSecondaryKeyword(
    list: string[],
    draft: string,
    mainKeyword: string,
): {keywords: string[]; draft: string} {
    const keywords = Array.isArray(list) ? list.slice() : [];
    const word = String(draft || '').replace(/\s+/g, ' ').trim();
    if (!word) {
        return {keywords, draft: ''};
    }
    const key = normalizeKeywordPhrase(word);
    if (!key || key === normalizeKeywordPhrase(mainKeyword)) {
        return {keywords, draft: ''};
    }
    if (keywords.some((item) => normalizeKeywordPhrase(item) === key)) {
        return {keywords, draft: ''};
    }
    keywords.push(word);
    return {keywords, draft: ''};
}

export function furthestPhase(phase: CreateSetupPhase, returnTo: ArticleChatReturnTo): ArticleChatDraftPhase {
    if (phase === 'resume') {
        return 'topic';
    }
    if (returnTo && phaseRank(returnTo) > phaseRank(phase)) {
        return returnTo;
    }
    return phase;
}

export function readArticleChatDraft(
    storage: DraftStorage | null | undefined,
    key: string = ARTICLE_CHAT_DRAFT_KEY,
): ArticleChatDraft | null {
    if (!storage || typeof storage.getItem !== 'function') {
        return null;
    }
    try {
        const raw = storage.getItem(key);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as Partial<ArticleChatDraft>;
        const aiPrompt = typeof parsed.aiPrompt === 'string' ? parsed.aiPrompt : '';
        const url = typeof parsed.url === 'string' ? parsed.url : '';
        const phase: ArticleChatDraftPhase = DRAFT_PHASES.indexOf(parsed.phase as ArticleChatDraftPhase) >= 0
            ? parsed.phase as ArticleChatDraftPhase
            : 'topic';
        const analyzeDraft = key === ARTICLE_CHAT_ANALYZE_DRAFT_KEY;
        if (!aiPrompt.trim() && !(analyzeDraft && url.trim())) {
            return null;
        }
        const location = normalizeLocale(
            typeof parsed.locationCode === 'string' ? parsed.locationCode : ARTICLE_CHAT_DEFAULT_LOCATION,
            typeof parsed.languageCode === 'string' ? parsed.languageCode : ARTICLE_CHAT_DEFAULT_LANGUAGE,
        );
        return {
            aiPrompt,
            phase,
            locationCode: location.locationCode,
            languageCode: location.languageCode,
            returnTo: asReturnTo(parsed.returnTo),
            keyword: typeof parsed.keyword === 'string' ? parsed.keyword : '',
            geoTemplate: normalizeGeoTemplate(typeof parsed.geoTemplate === 'string' ? parsed.geoTemplate : defaultWritingBrief.geoTemplate),
            geoTemplateUserPicked: parsed.geoTemplateUserPicked === true,
            tone: asChoice(parsed.tone, TONE_IDS, defaultWritingBrief.tone),
            audience: asChoice(parsed.audience, AUDIENCE_IDS, defaultWritingBrief.audience),
            depth: asChoice(parsed.depth, DEPTH_IDS, defaultWritingBrief.depth),
            secondaryKeywords: asSecondaryKeywords(parsed.secondaryKeywords),
            url,
            loadedTitle: typeof parsed.loadedTitle === 'string' ? parsed.loadedTitle : '',
            loadedHeadings: asLoadedHeadings(parsed.loadedHeadings),
            loadedUrl: typeof parsed.loadedUrl === 'string' ? parsed.loadedUrl : '',
            urlScrapeExtractionFailed: parsed.urlScrapeExtractionFailed === true,
            improveGoal: normalizeImproveGoal(parsed.improveGoal),
            sectionUids: asSectionUids(parsed.sectionUids),
        };
    } catch {
        return null;
    }
}

export function writeArticleChatDraft(
    storage: DraftStorage | null | undefined,
    draft: ArticleChatDraft,
    key: string = ARTICLE_CHAT_DRAFT_KEY,
): void {
    if (!storage || typeof storage.setItem !== 'function') {
        return;
    }
    storage.setItem(key, JSON.stringify({
        aiPrompt: draft.aiPrompt,
        phase: draft.phase,
        locationCode: draft.locationCode,
        languageCode: draft.languageCode,
        returnTo: draft.returnTo,
        keyword: draft.keyword,
        geoTemplate: draft.geoTemplate,
        geoTemplateUserPicked: draft.geoTemplateUserPicked,
        tone: draft.tone,
        audience: draft.audience,
        depth: draft.depth,
        secondaryKeywords: draft.secondaryKeywords,
        url: draft.url || '',
        loadedTitle: draft.loadedTitle || '',
        loadedHeadings: asLoadedHeadings(draft.loadedHeadings),
        loadedUrl: draft.loadedUrl || '',
        urlScrapeExtractionFailed: draft.urlScrapeExtractionFailed === true,
        improveGoal: normalizeImproveGoal(draft.improveGoal),
        sectionUids: asSectionUids(draft.sectionUids),
    }));
}

/** Skip rewrite goal when the URL scrape returned no outline to select. */
export function continueSetupPhaseAfterShell(
    current: ArticleChatStepId,
    returnTo: ArticleChatReturnTo,
    flow: readonly ArticleChatStepId[],
    options?: {skipGoalWithoutLoadedOutline?: boolean},
): Exclude<CreateSetupPhase, 'resume'> {
    let next = continueShell(current, returnTo, flow);
    if (options?.skipGoalWithoutLoadedOutline && next === 'goal') {
        next = 'summary';
    }
    return next;
}

export function clearArticleChatDraft(
    storage: DraftStorage | null | undefined,
    key: string = ARTICLE_CHAT_DRAFT_KEY,
): void {
    if (!storage || typeof storage.removeItem !== 'function') {
        return;
    }
    storage.removeItem(key);
}

export function initialCreateSetup(input: {
    isAnalyze: boolean;
    queryPrompt: string;
    queryUrl?: string;
    draft: ArticleChatDraft | null;
}): {phase: CreateSetupPhase; aiPrompt: string; url: string} {
    const queryPrompt = String(input.queryPrompt || '').trim();
    const queryUrl = String(input.queryUrl || '').trim();
    if (input.isAnalyze) {
        if (queryUrl || queryPrompt) {
            return {phase: 'url', aiPrompt: queryPrompt, url: queryUrl};
        }
        if (input.draft && (input.draft.aiPrompt.trim() || String(input.draft.url || '').trim())) {
            return {
                phase: 'resume',
                aiPrompt: input.draft.aiPrompt,
                url: String(input.draft.url || ''),
            };
        }
        return {phase: 'url', aiPrompt: '', url: ''};
    }
    if (queryPrompt) {
        return {phase: 'topic', aiPrompt: queryPrompt, url: ''};
    }
    if (input.draft && input.draft.aiPrompt.trim()) {
        return {phase: 'resume', aiPrompt: input.draft.aiPrompt, url: String(input.draft.url || '')};
    }
    return {phase: 'topic', aiPrompt: '', url: ''};
}

export function phaseAfterResume(draftPhase: ArticleChatDraftPhase, accept: boolean): CreateSetupPhase {
    if (!accept || draftPhase === 'topic') {
        return 'topic';
    }
    return draftPhase;
}

export function continueShell(
    current: ArticleChatStepId,
    returnTo: ArticleChatReturnTo,
    flow: readonly ArticleChatStepId[] = ARTICLE_CHAT_FLOW,
): Exclude<CreateSetupPhase, 'resume'> {
    if (returnTo) {
        return returnTo;
    }
    const next = flow[flow.indexOf(current) + 1];
    return next || 'summary';
}

export function editFrom(
    current: CreateSetupPhase,
    target: ArticleChatStepId,
): {phase: ArticleChatStepId; returnTo: ArticleChatReturnTo} {
    if (current === 'summary' || current === 'form') {
        return {phase: target, returnTo: 'summary'};
    }
    if (
        (
            current === 'url'
            || current === 'loaded'
            || current === 'topic'
            || current === 'locale'
            || current === 'keyword'
            || current === 'template'
            || current === 'style'
            || current === 'goal'
        )
        && phaseRank(target) < phaseRank(current)
    ) {
        return {phase: target, returnTo: current};
    }
    return {phase: target, returnTo: null};
}

export function presentHistory(rows: HistoryRow[]): HistoryPresentation {
    if (rows.length >= 4) {
        return {
            folded: true,
            from: rows[0].step,
            to: rows[rows.length - 1].step,
            visible: [],
            panel: rows,
        };
    }
    return {
        folded: false,
        from: rows[0] ? rows[0].step : 0,
        to: rows.length ? rows[rows.length - 1].step : 0,
        visible: rows.slice(-3),
        panel: rows,
    };
}

/** Short screens and an open keyboard keep a single summary line above the field. */
export function foldHistoryWhenRaised(
    view: HistoryPresentation,
    raised: boolean,
): HistoryPresentation {
    if (!raised || view.folded || view.visible.length < 2) {
        return view;
    }
    return {
        folded: true,
        from: view.visible[0].step,
        to: view.visible[view.visible.length - 1].step,
        visible: [],
        panel: view.panel,
    };
}

type HistorySource = {
    phase: CreateSetupPhase;
    returnTo: ArticleChatReturnTo;
    rows: HistoryRow[];
    flow?: readonly ArticleChatStepId[];
};

export function enteredSteps(input: HistorySource): HistoryRow[] {
    const rank = phaseRank(furthestPhase(input.phase, input.returnTo));
    return input.rows.filter((row) => phaseRank(row.id) >= 0 && phaseRank(row.id) < rank);
}

/**
 * Finished steps above the current question.
 * Step 1 shows none, including after Change from the review.
 * The review replaces this list.
 */
export function compactHistory(input: HistorySource): HistoryRow[] {
    const flow = input.flow && input.flow.length ? input.flow : ARTICLE_CHAT_FLOW;
    if (
        input.phase === 'summary'
        || input.phase === 'resume'
        || input.phase === 'form'
        || input.phase === flow[0]
    ) {
        return [];
    }
    return enteredSteps(input).filter((row) => row.id !== input.phase);
}

export function prefersReducedMotion(
    win: {matchMedia?: (query: string) => {matches: boolean}} | null | undefined,
): boolean {
    if (!win || typeof win.matchMedia !== 'function') {
        return true;
    }
    return win.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function topicPreview(value: string, max = 80): string {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (text.length <= max) {
        return text;
    }
    return `${text.slice(0, max - 1).trimEnd()}…`;
}

type ChatLocation = {
    location_code: number;
    location_name: string;
    country_iso_code?: string;
    available_languages: {language_name: string; language_code: string}[];
};

type LocaleOption = {value: string; text: string};

function chatLocations(): ChatLocation[] {
    return LocationLanguages as ChatLocation[];
}

function findChatLocation(locationCode: string): ChatLocation | undefined {
    return chatLocations().find((row) => String(row.location_code) === String(locationCode));
}

function displayName(uiLang: string, kind: 'region' | 'language', code: string): string {
    try {
        const name = new Intl.DisplayNames([uiLang || 'cs'], {type: kind}).of(code);
        if (name && name.toLowerCase() !== code.toLowerCase()) {
            return name;
        }
    } catch {
        return '';
    }
    return '';
}

export function countryLabel(locationCode: string, uiLang: string): string {
    const row = findChatLocation(locationCode);
    if (!row) {
        return '';
    }
    if (row.country_iso_code) {
        const localized = displayName(uiLang, 'region', row.country_iso_code);
        if (localized) {
            return localized;
        }
    }
    return row.location_name;
}

export function languageLabel(languageCode: string, fallback: string, uiLang: string): string {
    const localized = displayName(uiLang, 'language', languageCode);
    return localized || fallback;
}

export function localeChoiceLabel(
    locationCode: string,
    languageCode: string,
    uiLang: string,
): {country: string; language: string} {
    const row = findChatLocation(locationCode);
    const fallback = row?.available_languages.find((lang) => lang.language_code === languageCode)?.language_name
        || languageCode;
    return {
        country: countryLabel(locationCode, uiLang),
        language: languageLabel(languageCode, fallback, uiLang),
    };
}

export function locationOptions(uiLang: string): LocaleOption[] {
    return chatLocations().map((row) => ({
        value: String(row.location_code),
        text: countryLabel(String(row.location_code), uiLang),
    }));
}

export function languageOptions(locationCode: string, uiLang: string): LocaleOption[] {
    const row = findChatLocation(locationCode);
    if (!row) {
        return [];
    }
    return row.available_languages.map((lang) => ({
        value: lang.language_code,
        text: languageLabel(lang.language_code, lang.language_name, uiLang),
    }));
}

export function normalizeLocale(
    locationCode: string,
    languageCode: string,
): {locationCode: string; languageCode: string} {
    const row = findChatLocation(locationCode) || findChatLocation(ARTICLE_CHAT_DEFAULT_LOCATION);
    const location = row ? String(row.location_code) : ARTICLE_CHAT_DEFAULT_LOCATION;
    const languages = row ? row.available_languages : [];
    const match = languages.find((lang) => lang.language_code === languageCode);
    const language = match
        ? match.language_code
        : (languages[0] ? languages[0].language_code : ARTICLE_CHAT_DEFAULT_LANGUAGE);
    return {locationCode: location, languageCode: language};
}

/** Short screens and an open keyboard move the field up so Continue stays reachable. */
export function viewportRaisesChat(input: {
    innerHeight: number;
    viewportHeight: number;
    maxHeightMatches: boolean;
}): boolean {
    if (input.maxHeightMatches) {
        return true;
    }
    if (input.viewportHeight > 0 && input.viewportHeight < 560) {
        return true;
    }
    return input.innerHeight - input.viewportHeight > 120;
}
